import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CircleHelp, Sparkles } from "lucide-react";
import { AppCanvas } from "@/components/app/AppCanvas";
import { NextSceneCard } from "@/components/app/NextSceneCard";
import { ScenePath, type ScenePathItem } from "@/components/app/ScenePath";
import { Wordmark } from "@/components/landing/Wordmark";
import { CATALOG_SITUATIONS } from "@/data/catalogScenarios";
import { supabase } from "@/integrations/supabase/client";
import { findCatalogSituation } from "@/lib/catalog";
import { FIRST_SCENE_SLUG } from "@/lib/firstScene";
import {
  FEATURED_MISSIONS,
  featuredMissionForSituation,
  missionProgressFromSessions,
  missionSlug,
  sceneCardSummary,
} from "@/lib/missions";
import { isInterpreterAnalysis } from "@/lib/interpreter";
import { parseSessionAnalysis } from "@/lib/sessionAnalysis";
import { UpgradeBanner } from "@/components/paywall";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useToast } from "@/hooks/use-toast";

type SessionRow = {
  catalog_slug: string | null;
  analysis: unknown;
  status: string | null;
};

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { refresh, isPaid } = useSubscription();
  const [rows, setRows] = useState<SessionRow[]>([]);
  const sceneParam = searchParams.get("scene");

  useEffect(() => {
    const upgradeStatus = searchParams.get("upgrade");
    if (upgradeStatus === "success") {
      toast({
        title: "Assinatura ativa!",
        description: "Conversas liberadas na assinatura — sem cota mensal.",
      });
      refresh();
      searchParams.delete("upgrade");
      setSearchParams(searchParams, { replace: true });
    } else if (upgradeStatus === "canceled") {
      toast({
        title: "Assinatura cancelada",
        description: "Você pode tentar de novo quando quiser.",
        variant: "destructive",
      });
      searchParams.delete("upgrade");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast, refresh]);

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("conversation_sessions")
        .select("catalog_slug, analysis, status")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(40);
      setRows((data || []) as SessionRow[]);
    };
    void load();
  }, []);

  const progress = useMemo(
    () => missionProgressFromSessions(rows, CATALOG_SITUATIONS),
    [rows],
  );

  const practiceRows = useMemo(
    () => rows.filter((row) => !isInterpreterAnalysis(row.analysis)),
    [rows],
  );
  const lastSlug = practiceRows[0]?.catalog_slug ?? null;
  const requested = findCatalogSituation(CATALOG_SITUATIONS, sceneParam ?? undefined);
  const lastSituation = findCatalogSituation(CATALOG_SITUATIONS, lastSlug ?? undefined);
  const situation = requested
    ?? lastSituation
    ?? findCatalogSituation(CATALOG_SITUATIONS, FIRST_SCENE_SLUG)!;

  const mission = featuredMissionForSituation(situation);
  const missionProgress = mission ? progress[mission.id] : undefined;
  const lastAnalysis = practiceRows.find((row) => row.catalog_slug === situation.slug);
  const analysis = parseSessionAnalysis(lastAnalysis?.analysis);
  const practicedThisScene = Boolean(missionProgress?.rehearsals || lastAnalysis);
  const summaryLine = sceneCardSummary(
    situation,
    analysis,
    practicedThisScene,
    lastAnalysis?.status,
  );

  const pathItems = useMemo<ScenePathItem[]>(
    () =>
      FEATURED_MISSIONS
        .filter((item) => item.id !== mission?.id)
        .map((item) => {
          const scene = findCatalogSituation(
            CATALOG_SITUATIONS,
            progress[item.id].lastSlug ?? missionSlug(item),
          );
          return scene ? { mission: item, scene, status: progress[item.id].status } : null;
        })
        .filter((item): item is ScenePathItem => item !== null),
    [mission?.id, progress],
  );

  return (
    <AppCanvas className="pb-8 pt-5 md:pt-8">
      <header className="flex items-center justify-between">
        <Wordmark to="/app" size="sm" className="md:hidden" />
        <nav className="flex items-center gap-5 text-label">
          <span className="text-neutral-900">Cenas</span>
          <Link to="/app/practice" className="text-neutral-500 transition hover:text-neutral-900">
            Praticar
          </Link>
        </nav>
        <div className="flex items-center gap-1">
          <Link
            to="/app/ajuda"
            aria-label="Ajuda"
            className="flex h-11 w-11 items-center justify-center text-neutral-700"
          >
            <CircleHelp className="h-6 w-6" strokeWidth={2} />
          </Link>
          <Link
            to="/app/upgrade"
            aria-label={isPaid ? "Assinatura" : "Assinar"}
            className="flex h-11 items-center gap-1 px-2 text-label text-blue-600"
          >
            <Sparkles className="h-5 w-5" strokeWidth={2} />
            {isPaid ? "Assinatura" : "Assinar"}
          </Link>
        </div>
      </header>

      <UpgradeBanner className="mt-5" />

      <p className="mt-7 text-overline text-neutral-500">
        Missão de hoje
      </p>
      <h1 className="mt-1.5 text-h1 text-neutral-900">
        {mission?.placeTitle ?? situation.title}
      </h1>

      <div className="mt-5">
        <NextSceneCard
          situation={situation}
          ctaLabel={isPaid ? "Começar o ensaio" : "Assinar para ensaiar"}
          summaryLine={summaryLine}
          to={isPaid ? undefined : "/app/upgrade"}
        />
      </div>

      <div className="mt-6">
        <ScenePath items={pathItems} />
      </div>
    </AppCanvas>
  );
};

export default Dashboard;
